<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { IonContent, IonRefresher, IonRefresherContent, IonSpinner } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

import AccordionList from '@/shared/components/ui/accordionlist/AccordionList.vue';
import AccordionListItem from '@/shared/components/ui/accordionlist/AccordionListItem.vue';
import AccordionListItemAction from '@/shared/components/ui/accordionlist/AccordionListItemAction.vue';
import TopBar from '@/shared/components/TopBar.vue';
import BackButton from '@/shared/components/BackButton.vue';
import TransactionOverview from '../components/TransactionOverview.vue';
import type { Order, WalletTransaction } from '@/shared/interfaces/wallet';
import useTenant from '@/shared/composables/useTenant';
import useWallet from '@/modules/wallet/composables/useWallet';
import useUtils from '@/shared/composables/useUtils';

// Init
const route = useRoute();
const walletId = route.params.wallet_id?.toString();
const encryptedRfidTag = route.query.id?.toString();
const { getWalletTransactions, getTransactionItemName } = useWallet();
const { t, te } = useI18n();
const mappedWalletTransactions = ref<any>([]);
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { refetchGetWalletTransactions, removeGetWalletTransactionsResultsFromCache, walletTransactions } = getWalletTransactions(encryptedRfidTag, walletId);
const { groupAndSumTransactionsByCurrency } = useUtils();

// Refresh
const handleRefresh = async (event: any) => {
	removeGetWalletTransactionsResultsFromCache();
	mappedWalletTransactions.value = [];
	await refetchGetWalletTransactions();
	event.target.complete();
};

// Watch for walletTransactions then map the result
watchEffect(() => {
	if (walletTransactions.value && walletTransactions.value && walletTransactions.value.data && walletTransactions.value.data.length) {
		mappedWalletTransactions.value = walletTransactions.value.data.map((walletTransaction: WalletTransaction) => {
			const typeMap: Record<string, string> = { topup: 'top_up', top_up_cost: 'top_up_cost', voucher_top_up: 'voucher_top_up' };
			const typeKey = typeMap[walletTransaction.type] || walletTransaction.type;
			let title = te(`components.transaction.type.${typeKey}`) ? t(`components.transaction.type.${typeKey}`) : walletTransaction.type.replaceAll('_', ' ');
			let subtitle = '';

			if (walletTransaction.is_split_payment || walletTransaction.type === 'split_payment') {
				title = t('wallet.split_payment');
				subtitle = t('wallet.split_payment_subtitle');
			}

			return {
				id: walletTransaction.id,
				title: title,
				type: walletTransaction.type,
				subtitle: subtitle,
				iconClass: 'text-white',
				price: walletTransaction.order_price ? +walletTransaction.order_price : 0,
				currency: walletTransaction.currency,
				date: walletTransaction.created_at,
				transactionType: walletTransaction.direction,
				transactionsPerCurrency: groupAndSumTransactionsByCurrency(
					// For split payments, only show transactions for this wallet
					walletTransaction.is_split_payment || walletTransaction.type === 'split_payment' ? (walletTransaction.overview?.transactions || []).filter((t) => t.wallet_id === walletTransaction.wallet_id) : walletTransaction.overview?.transactions || [],
				),
				first_virtual_wallet_transaction: walletTransaction.first_virtual_wallet_transaction,
				// Add split payment info
				isSplitPayment: walletTransaction.is_split_payment || walletTransaction.type === 'split_payment',
				splitPaymentInfo: walletTransaction.split_payment,
				totalOrderPrice: walletTransaction.total_order_price,
				locationName: walletTransaction.order?.location_name,
			};
		});
	}
});


</script>

<template>
	<ion-content>
		<ion-refresher class="mt-8" slot="fixed" @ionRefresh="handleRefresh($event)">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>

		<TopBar class="px-[34px]">
			<template #left>
				<BackButton />
			</template>

			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('wallet.history') }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px]">
			<AccordionList :title="t('wallet.all_transactions')" class="mb-4">
				<template #items>
					<div v-if="!mappedWalletTransactions.length && mappedWalletTransactions.length !== 0" class="flex p-4 items-center justify-center">
						<ion-spinner name="dots"></ion-spinner>
					</div>
					<div v-if="!mappedWalletTransactions.length && mappedWalletTransactions.length === 0" class="flex p-4 items-center justify-center font-sourcesanspro-regular">
						{{ t('wallet.history_no_transactions') }}
					</div>

					<!-- Father item -->
					<AccordionListItem v-for="(item, idx) in mappedWalletTransactions" :key="idx" :key-value="item.id" :icon="require('@/statics/svg/arrows-left-right.svg')" :iconClass="item.iconClass" :subtitle="item.subtitle" :customSubtitle="item.locationName" :title="te(item.title) ? t(item.title) : item.title" :transaction-type="item.transactionType">
						<template v-slot:actions>
							<AccordionListItemAction v-if="tenantConfig" :key="item.id" :date="DateTime.fromISO(item.date).toFormat('dd LLLL yyyy HH:mm:ss')" :price="item.price" :price-detail="item.transactionsPerCurrency" :currency="item.currency" :transaction-type="item.transactionType" :payment-type="item.type" :hide-transaction-sign="false" :tenant-config="tenantConfig" />
						</template>
						<template v-slot:content="slotProps">
							<TransactionOverview v-if="tenantConfig && item.type !== 'wallet_merge'" :key="item.id" :transaction-id="item.first_virtual_wallet_transaction ? item.first_virtual_wallet_transaction?.id : item.id" :encryptedRfidTag="encryptedRfidTag" :should-load="slotProps.shouldLoad" :tenant-config="tenantConfig" />
						</template>
					</AccordionListItem>
				</template>
			</AccordionList>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
